import React, { useState, useEffect, useCallback } from 'react';
import './ApplyNowModal.css';
import { getMobileNumber, applyForJob, getCandidateByMobile, downloadResume, viewResumeFile, updateCandidateResume, saveCandidateDetails } from '../services/apiService';
import { useToast } from '../contexts/ToastContext';

const ApplyNowModal = ({ isOpen, onClose, job, onSubmit, onSuccess }) => {
    const [applicationData, setApplicationData] = useState({
        fullName: '', // Will be filled from user profile
        email: '', // Will be filled from user profile
        mobile: '', // Will be filled from user profile
        experience: '', // Years of experience
        currentLocation: '', // Current location
        expectedSalary: '', // Expected salary
        noticePeriod: '', // Notice period
        newResume: null
    });

    const [showCurrentResume] = useState(true); // Show current resume section
    const [currentResumeFileName, setCurrentResumeFileName] = useState('Resume.pdf');
    const [currentResumeFilePath, setCurrentResumeFilePath] = useState(null);
    const [currentResumeCandidateId, setCurrentResumeCandidateId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [downloadingResume, setDownloadingResume] = useState(false);
    const [viewingResume, setViewingResume] = useState(false);
    const [uploadingResume, setUploadingResume] = useState(false);
    const [showCurrentResumePreview, setShowCurrentResumePreview] = useState(false);
    const [currentResumeObjectUrl, setCurrentResumeObjectUrl] = useState(null);
    const { showSuccess, showError } = useToast();

    // Normalize a person's full name by collapsing spaces and trimming.
    // If name is not provided, attempt to build from first/middle/last parts (if present on the profile obj)
    const normalizeFullName = (name, profileObj = null) => {
        const sanitize = (value) => {
            if (typeof value !== 'string') return '';
            const trimmed = value.replace(/\s+/g, ' ').trim();
            if (!trimmed) return '';
            const lower = trimmed.toLowerCase();
            if (lower === 'null' || lower === 'undefined') return '';
            
            // Check if the string consists only of "null" or "undefined" words
            // e.g., "null null", "undefined null", "null undefined undefined"
            const words = trimmed.split(/\s+/);
            const allInvalid = words.every(word => 
                word.toLowerCase() === 'null' || word.toLowerCase() === 'undefined'
            );
            if (allInvalid) return '';
            
            // Filter out individual "null" or "undefined" words from the name
            const validWords = words.filter(word => {
                const lowerWord = word.toLowerCase();
                return lowerWord !== 'null' && lowerWord !== 'undefined';
            });
            
            return validWords.join(' ');
        };

        try {
            const cleaned = sanitize(name);
            if (cleaned) return cleaned;

            const parts = [
                profileObj?.firstName,
                profileObj?.middleName,
                profileObj?.lastName,
            ]
                .map(sanitize)
                .filter(Boolean);

            return parts.join(' ');
        } catch (_) {
            return sanitize(name);
        }
    };

    

    // Load user profile data using new candidate profile API
    const loadUserProfile = useCallback(async () => {
        try {
            setLoading(true);
            const mobileNumber = getMobileNumber();
            console.log('[ApplyNowModal] Loading candidate profile, mobile number from service:', mobileNumber);
            
            // Always ensure we have a mobile number to display
            let finalMobileNumber = mobileNumber;
            
            // For demo purposes, use mock data if mobile is '0' or empty
            if (!mobileNumber || mobileNumber === '0' || mobileNumber === '') {
                const mockProfile = {
                    fullName: 'John Doe',
                    email: 'john.doe@example.com',
                    mobile: '+91 98765 43210',
                    resumeFileName: 'John_Doe_Resume.pdf'
                };
                setApplicationData(prev => ({
                    ...prev,
                    fullName: normalizeFullName(mockProfile.fullName, mockProfile),
                    email: mockProfile.email,
                    mobile: mockProfile.mobile
                }));
                setCurrentResumeFileName(mockProfile.resumeFileName);
                console.log('[ApplyNowModal] Using mock profile data:', {
                    fullName: mockProfile.fullName,
                    email: mockProfile.email,
                    mobile: mockProfile.mobile
                });
            } else {
                // Try to load actual profile using new candidate API
                try {
                    console.log('[ApplyNowModal] 🚀 About to call getCandidateByMobile()...');
                    const result = await getCandidateByMobile();
                    console.log('[ApplyNowModal] 📋 Candidate API response:', result);
                    
                    if (result.success && result.data) {
                        const profile = result.data;
                        finalMobileNumber = profile.mobile || mobileNumber;
                        setApplicationData(prev => ({
                            ...prev,
                            fullName: normalizeFullName(profile.fullName, profile),
                            email: profile.email || '',
                            mobile: finalMobileNumber
                        }));
                        // Determine resume file info
                        const filePath = profile.filePath;
                        const candidateId = profile.candidateId || profile.id;
                        setCurrentResumeFilePath(filePath || null);
                        setCurrentResumeCandidateId(candidateId || null);
                        if (filePath && typeof filePath === 'string' && filePath.trim() !== '' && filePath !== 'null') {
                            const nameFromPath = filePath.split('/').pop() || profile.resumeFileName;
                            setCurrentResumeFileName(nameFromPath || 'Resume.pdf');
                        } else {
                            setCurrentResumeFileName(profile.resumeFileName || 'Resume.pdf');
                        }
                        console.log('[ApplyNowModal] Loaded candidate profile data:', {
                            fullName: profile.fullName || '',
                            email: profile.email || '',
                            mobile: finalMobileNumber
                        });
                    } else if (result.success && result.status === 404) {
                        // Candidate not found (404) - use mobile number with empty name/email
                        setApplicationData(prev => ({
                            ...prev,
                            fullName: '',
                            email: '',
                            mobile: mobileNumber
                        }));
                        setCurrentResumeFileName('Resume.pdf');
                        setCurrentResumeFilePath(null);
                        setCurrentResumeCandidateId(null);
                        console.log('[ApplyNowModal] Candidate not found (404), using mobile only:', mobileNumber);
                    } else {
                        // API failed, use mobile number directly
                        setApplicationData(prev => ({
                            ...prev,
                            fullName: '',
                            email: '',
                            mobile: mobileNumber
                        }));
                        setCurrentResumeFileName('Resume.pdf');
                        console.log('[ApplyNowModal] Candidate API failed, using mobile number:', mobileNumber);
                    }
                } catch (profileError) {
                    console.error('[ApplyNowModal] Error calling getCandidateByMobile:', profileError);
                    // Use mobile number as fallback
                    setApplicationData(prev => ({
                        ...prev,
                        fullName: '',
                        email: '',
                        mobile: mobileNumber
                    }));
                    setCurrentResumeFileName('Resume.pdf');
                    console.log('[ApplyNowModal] Profile error, using mobile number:', mobileNumber);
                }
            }
        } catch (error) {
            console.error('[ApplyNowModal] Error in loadUserProfile:', error);
            // Final fallback - try to get mobile number again
            try {
                const fallbackMobile = getMobileNumber();
                if (fallbackMobile && fallbackMobile !== '0') {
                    setApplicationData(prev => ({
                        ...prev,
                        fullName: '',
                        email: '',
                        mobile: fallbackMobile
                    }));
                    console.log('[ApplyNowModal] Using fallback mobile:', fallbackMobile);
                } else {
                    // Use demo mobile if nothing else works
                    setApplicationData(prev => ({
                        ...prev,
                        fullName: '',
                        email: '',
                        mobile: '+91 98765 43210'
                    }));
                    console.log('[ApplyNowModal] Using demo mobile as final fallback');
                    setCurrentResumeFileName('Resume.pdf');
                    setCurrentResumeFilePath(null);
                    setCurrentResumeCandidateId(null);
                }
            } catch (fallbackError) {
                console.error('[ApplyNowModal] Fallback mobile error:', fallbackError);
                // Absolute fallback
                setApplicationData(prev => ({
                    ...prev,
                    fullName: '',
                    email: '',
                    mobile: '+91 98765 43210'
                }));
            }
        } finally {
            setLoading(false);
        }
    }, []);

    // Handle body overflow when modal opens/closes and load user profile
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            loadUserProfile();
        } else {
            document.body.style.overflow = 'auto';
        }
        
        // Cleanup on unmount
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen, loadUserProfile]);

    if (!isOpen) return null;

    const handleInputChange = (field, value) => {
        // Prevent mobile number changes - it should be readonly
        if (field === 'mobile') {
            return;
        }
        const nextValue = field === 'fullName' ? normalizeFullName(value) : value;
        setApplicationData(prev => ({
            ...prev,
            [field]: nextValue
        }));
    };

    const handleNewResumeUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!allowedTypes.includes(file.type)) {
            showError('Please upload a PDF, DOC, or DOCX file.');
            return;
        }
        
        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            showError('File size must be less than 5MB.');
            return;
        }

        // Check if we have a candidate ID to upload the resume
        if (!currentResumeCandidateId) {
            showError('Unable to upload resume. Please try again later.');
            console.error('[ApplyNowModal] No candidate ID available for resume upload');
            return;
        }

        try {
            setUploadingResume(true);
            console.log('[ApplyNowModal] Starting resume upload for candidate:', currentResumeCandidateId);

            // Build upload filename: <candidateId>_Resume.<ext>
            const origName = file.name || '';
            const lastDot = origName.lastIndexOf('.');
            const ext = lastDot !== -1 ? origName.slice(lastDot) : '';
            const uploadFileName = `${currentResumeCandidateId}_Resume${ext}`;

            // Call the updateCandidateResume API immediately
            const result = await updateCandidateResume(currentResumeCandidateId, file, uploadFileName);
            
            if (result.success) {
                console.log('[ApplyNowModal] Resume uploaded successfully:', result);
                showSuccess('Resume uploaded successfully!');
                
                // Refresh candidate details to get updated resume info
                try {
                    console.log('[ApplyNowModal] Refreshing candidate details after resume upload...');
                    const candidateResult = await getCandidateByMobile();
                    
                    if (candidateResult.success && candidateResult.data) {
                        const profile = candidateResult.data;
                        console.log('[ApplyNowModal] Updated candidate profile:', profile);
                        
                        // Update current resume info with fresh data from API
                        const filePath = profile.filePath;
                        setCurrentResumeFilePath(filePath || null);
                        
                        if (filePath && typeof filePath === 'string' && filePath.trim() !== '' && filePath !== 'null') {
                            const nameFromPath = filePath.split('/').pop() || profile.resumeFileName;
                            setCurrentResumeFileName(nameFromPath || uploadFileName);
                        } else {
                            setCurrentResumeFileName(profile.resumeFileName || uploadFileName);
                        }
                        
                        console.log('[ApplyNowModal] Updated resume info:', {
                            fileName: profile.resumeFileName || uploadFileName,
                            filePath: filePath
                        });
                    } else {
                        console.warn('[ApplyNowModal] Failed to refresh candidate details, using upload result');
                        // Fallback to using upload result data
                        setCurrentResumeFileName(uploadFileName);
                        setCurrentResumeFilePath(result.data?.filePath || null);
                    }
                } catch (refreshError) {
                    console.error('[ApplyNowModal] Error refreshing candidate details:', refreshError);
                    // Fallback to using upload result data
                    setCurrentResumeFileName(uploadFileName);
                    setCurrentResumeFilePath(result.data?.filePath || null);
                }
                
                // Clear any previous new resume data since it's now the current resume
                setApplicationData(prev => ({ ...prev, newResume: null }));
                
                // Reset file input
                const fileInput = document.getElementById('newResumeFile');
                if (fileInput) fileInput.value = '';
                
            } else {
                console.error('[ApplyNowModal] Resume upload failed:', result);
                showError(result.message || 'Failed to upload resume. Please try again.');
            }
            
        } catch (error) {
            console.error('[ApplyNowModal] Error uploading resume:', error);
            showError(`Error uploading resume: ${error.message}. Please try again.`);
        } finally {
            setUploadingResume(false);
        }
    };


    const handleClose = () => {
        // Reset form
        setApplicationData({
            fullName: '',
            email: '',
            mobile: '',
            newResume: null
        });
        if (currentResumeObjectUrl) {
            URL.revokeObjectURL(currentResumeObjectUrl);
            setCurrentResumeObjectUrl(null);
        }
        setShowCurrentResumePreview(false);
        setCurrentResumeFilePath(null);
        setCurrentResumeCandidateId(null);
        
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        console.log('[ApplyNowModal] Form submission started');
        console.log('[ApplyNowModal] Application data:', applicationData);
        console.log('[ApplyNowModal] Job data:', job);
        
        // Validate required fields (Email is optional)
        if (!applicationData.fullName.trim() || !applicationData.mobile.trim()) {
            console.log('[ApplyNowModal] Validation failed: Missing required fields');
            showError('Please fill in all required fields.');
            return;
        }

        // Normalize and validate full name: letters and spaces only, 2-50 chars
        const fullNameTrimmed = normalizeFullName(applicationData.fullName);
        const nameRegex = /^[A-Za-z ]{2,50}$/;
        if (!nameRegex.test(fullNameTrimmed)) {
            console.log('[ApplyNowModal] Validation failed: Invalid full name');
            showError('Full Name should contain letters and spaces only (2-50 characters).');
            return;
        }
        
        // Validate email format only if provided (email is optional)
        if (applicationData.email && applicationData.email.trim() !== '') {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailRegex.test(applicationData.email.trim())) {
                console.log('[ApplyNowModal] Validation failed: Invalid email format');
                showError('Please enter a valid email address (e.g., user@example.com).');
                return;
            }
        }
        
        if (!job || (!job.positionId && !job.position_id && !job.id && !job.jobId)) {
            console.log('[ApplyNowModal] Validation failed: No position ID available');
            showError('Job information is missing. Please try again.');
            return;
        }
        
        try {
            setSubmitting(true);
            console.log('[ApplyNowModal] All validations passed, proceeding with API call');
            
            // Derive recruiterId robustly from job sources (URL params, nested originalJob)
            const recruiterIdFromJob = (
                job?.recruiterId ??
                job?.recruiter_id ??
                job?._params?.recruiterId ??
                job?.originalJob?.recruiterId ??
                job?.originalJob?._params?.recruiterId
            );

            // Prepare submission data
            const submissionData = {
                fullName: fullNameTrimmed,
                email: applicationData.email.trim(),
                mobile: applicationData.mobile.trim(),
                resumeFile: applicationData.newResume,
                useCurrentResume: !applicationData.newResume,
                submittedAt: new Date().toISOString(),
                // Forward recruiterId if present on job (from getActualUrl flow)
                recruiterId: (recruiterIdFromJob !== undefined && recruiterIdFromJob !== null && recruiterIdFromJob !== '')
                    ? String(recruiterIdFromJob)
                    : undefined
            };
            
            console.log('[ApplyNowModal] recruiterId candidates:', {
                fromJob: job?.recruiterId,
                fromJobSnake: job?.recruiter_id,
                fromParams: job?._params?.recruiterId,
                fromOriginalJob: job?.originalJob?.recruiterId,
                fromOriginalParams: job?.originalJob?._params?.recruiterId,
                final: submissionData.recruiterId
            });
            console.log('[ApplyNowModal] Submitting application:', submissionData);
            
            // Get position ID from job object (matching V2 implementation)
            const positionId = job.positionId || job.position_id || job.id || job.jobId;
            
            console.log('[ApplyNowModal] Job source:', job.originalNotification ? 'Notification' : 'Regular');
            console.log('[ApplyNowModal] Using position ID:', positionId);
            console.log('[ApplyNowModal] Available IDs for reference:', {
                budgetCityId: job.budgetCityId,
                budgetId: job.budgetId,
                positionId: job.positionId,
                position_id: job.position_id,
                id: job.id,
                jobId: job.jobId
            });

            // Validate position ID - especially important for notification jobs
            if (!positionId) {
                console.error('[ApplyNowModal] No valid position ID found for application');
                showError('Unable to apply: Position ID not available. Please try again.');
                return;
            }
            
            // Step 1: Save candidate details first
            console.log('[ApplyNowModal] Step 1: Saving candidate details...');
            const candidateData = {
                fullName: fullNameTrimmed,
                email: applicationData.email.trim(),
                experience: applicationData.experience || '',
                currentLocation: applicationData.currentLocation || '',
                expectedSalary: applicationData.expectedSalary || '',
                noticePeriod: applicationData.noticePeriod || ''
            };
            
            const candidateResult = await saveCandidateDetails(candidateData);
            console.log('[ApplyNowModal] Candidate save result:', candidateResult);
            
            if (!candidateResult.success) {
                console.error('[ApplyNowModal] Failed to save candidate details:', candidateResult.error);
                showError(`Failed to save candidate details: ${candidateResult.error}. Please try again.`);
                return;
            }
            
            console.log('[ApplyNowModal] Candidate details saved successfully, proceeding with job application...');
            
            // Dispatch event to update profile icon initials in Header
            try {
                window.dispatchEvent(new CustomEvent('userProfileUpdated', {
                    detail: { fullName: fullNameTrimmed }
                }));
                console.log('[ApplyNowModal] Dispatched userProfileUpdated event with name:', fullNameTrimmed);
            } catch (eventError) {
                console.warn('[ApplyNowModal] Failed to dispatch userProfileUpdated event:', eventError);
            }
            
            // Step 2: Apply for job after candidate details are saved
            console.log('[ApplyNowModal] Step 2: Applying for job...');
            const result = await applyForJob(positionId, submissionData);
            console.log('[ApplyNowModal] API response:', result);
            
            if (result.success) {
                console.log('[ApplyNowModal] Application submitted successfully');
                const jobTitle = job.jobName || job.title || 'this position';

                // Call parent onSubmit if provided (for additional handling)
                if (onSubmit) {
                    try {
                        await onSubmit(job, submissionData);
                    } catch (parentError) {
                        console.warn('[ApplyNowModal] Parent onSubmit failed:', parentError);
                    }
                }
                // Notify parent first (to allow parent modal to close)
                try {
                    if (typeof onSuccess === 'function') {
                        onSuccess();
                    }
                } catch (cbErr) {
                    console.warn('[ApplyNowModal] onSuccess callback error:', cbErr);
                }
                // Close this modal immediately
                handleClose();
                // Then show success toast (non-blocking)
                showSuccess(`Application submitted successfully for ${jobTitle}!`);

            } else {
                console.log('[ApplyNowModal] Application failed:', result.message);
                const errorMessage = result.message || 'Failed to submit application. Please try again.';
                
                // Close modal for position-related errors (position closed, no vacancies, etc.)
                const isPositionUnavailable = errorMessage.includes('no longer accepting') || 
                                              errorMessage.includes('positions have been filled') ||
                                              errorMessage.includes('not available for applications');
                if (isPositionUnavailable) {
                    // Close both ApplyNowModal and parent JobDetailsModal
                    handleClose();
                    if (typeof onSuccess === 'function') {
                        onSuccess(); // This closes the parent JobDetailsModal too
                    }
                }
                showError(errorMessage);
            }
            
        } catch (error) {
            console.error('[ApplyNowModal] Error submitting application:', error);
            console.error('[ApplyNowModal] Error details:', {
                name: error.name,
                message: error.message,
                stack: error.stack
            });
            showError(`An error occurred while submitting your application: ${error.message}. Please try again.`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            if (submitting || downloadingResume || uploadingResume) return; // block close during critical actions
            handleClose();
        }
    };

    // View the current resume within the modal as thumbnail
    const handleViewCurrentResume = async () => {
        try {
            if (!currentResumeFilePath || !currentResumeCandidateId) {
                showError('No resume available to view.');
                return;
            }
            setViewingResume(true);
            const result = await viewResumeFile(currentResumeCandidateId, currentResumeFilePath);
            if (result?.success && result?.data?.objectUrl) {
                setCurrentResumeObjectUrl(result.data.objectUrl);
                setShowCurrentResumePreview(true);
            } else {
                showError(result?.error || 'Failed to load resume for viewing.');
            }
        } catch (err) {
            console.error('[ApplyNowModal] Error viewing current resume:', err);
            showError('Error loading resume. Please try again.');
        } finally {
            setViewingResume(false);
        }
    };

    // Download the current resume from server
    const handleDownloadCurrentResume = async () => {
        try {
            if (!currentResumeFilePath || !currentResumeCandidateId) {
                showError('No resume available to download.');
                return;
            }
            setDownloadingResume(true);
            const result = await downloadResume(currentResumeCandidateId, currentResumeFilePath);
            if (!result?.success) {
                showError(result?.error || 'Failed to download resume.');
            }
        } catch (err) {
            console.error('[ApplyNowModal] Error downloading current resume:', err);
            showError('Error downloading resume. Please try again.');
        } finally {
            setDownloadingResume(false);
        }
    };

    return (
        <div className="apply-modal-overlay" onClick={handleOverlayClick}>
            <div className="apply-modal-container">
                {/* Fixed Modal Header */}
                <div className="apply-modal-header">
                    <div className="apply-modal-header-content">
                        <div>
                            <h2 className="apply-modal-title">Apply for Job</h2>
                            <p className="apply-modal-subtitle">Submit your application</p>
                        </div>
                        <button onClick={handleClose} className="apply-modal-close-btn" disabled={submitting || downloadingResume || uploadingResume}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                </div>

                {/* Modal Content */}
                <div className="apply-modal-content">
                    {loading ? (
                        <div className="apply-loading-state" style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            padding: '3rem 2rem',
                            textAlign: 'center'
                        }}>
                            <div className="loading-spinner" style={{
                                fontSize: '2rem',
                                color: '#2563eb',
                                marginBottom: '1rem'
                            }}>
                                <i className="fas fa-spinner fa-spin"></i>
                            </div>
                            <p style={{
                                color: '#6b7280',
                                fontSize: '0.875rem',
                                margin: '0',
                                fontWeight: '500'
                            }}>Loading your profile...</p>
                        </div>
                    ) : (
                    <form onSubmit={handleSubmit} aria-busy={submitting} className="apply-form">
                        {/* Scrollable Body Content */}
                        <div className="apply-scrollable-content">
                        {/* Personal Information Section */}
                        <div className="apply-section">
                            <h3 className="apply-section-title">
                                <i className="fas fa-user apply-section-icon"></i>
                                Personal Information
                            </h3>
                            
                            <div className="apply-form-grid">
                                <div className="apply-form-field">
                                    <label className="apply-form-label">Full Name*</label>
                                    {/* Debug: Show full name value */}
                                    {console.log('Rendering full name field with value:', applicationData.fullName)}
                                    <input
                                        type="text"
                                        value={applicationData.fullName || ''}
                                        onChange={(e) => handleInputChange('fullName', e.target.value)}
                                        required
                                        className="apply-form-input"
                                        placeholder="Enter your full name"
                                        style={{
                                            backgroundColor: applicationData.fullName ? '#f0f9ff' : '#ffffff',
                                            borderColor: applicationData.fullName ? '#2563eb' : '#d1d5db'
                                        }}
                                        maxLength="50"
                                        pattern="[A-Za-z ]{2,50}"
                                        title="Use letters and spaces only (2-50 characters)"
                                        autoComplete="off"
                                        onInput={(e) => {
                                            const cleaned = e.target.value
                                                .replace(/[^A-Za-z ]/g, '')
                                                .replace(/^\s+/, '')
                                                .replace(/\s{2,}/g, ' ');
                                            if (e.target.value !== cleaned) {
                                                e.target.value = cleaned;
                                            }
                                        }}
                                    />

                                </div>
                                <div className="apply-form-field">
                                    <label className="apply-form-label">Mobile Number*</label>
                                    {/* Debug: Show mobile number value */}
                                    {console.log('Rendering mobile field with value:', applicationData.mobile)}
                                    <input
                                        type="tel"
                                        value={applicationData.mobile || ''}
                                        readOnly
                                        required
                                        className="apply-form-input"
                                        placeholder="Your registered mobile number"
                                        style={{
                                            backgroundColor: '#f9fafb',
                                            cursor: 'not-allowed',
                                            color: '#374151', // Darker color for better visibility
                                            fontWeight: '500'
                                        }}
                                    />

                                </div>
                            </div>
                            
                            <div className="apply-form-field">
                                <label className="apply-form-label">Email Address (Optional)</label>
                                {/* Debug: Show email value */}
                                {console.log('Rendering email field with value:', applicationData.email)}
                                <input
                                    type="email"
                                    value={applicationData.email || ''}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                    className="apply-form-input"
                                    pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                                    title="Please enter a valid email address (e.g., user@example.com)"
                                    placeholder="Enter your email address (e.g., user@example.com)"
                                    style={{
                                        backgroundColor: applicationData.email ? '#f0f9ff' : '#ffffff',
                                        borderColor: applicationData.email ? '#2563eb' : '#d1d5db'
                                    }}
                                />

                            </div>
                        </div>

                        {/* Resume Section */}
                        <div className="apply-section">
                            <h3 className="apply-section-title">
                                <i className="fas fa-file-pdf apply-section-icon"></i>
                                Resume
                            </h3>
                            
                            {/* Current Resume Display - Only show if resume exists */}
                            {showCurrentResume && currentResumeFilePath && currentResumeCandidateId && (
                                <div className="current-resume-display">
                                    <div className="resume-item">
                                        <i className="fas fa-file-pdf resume-icon"></i>
                                        <div className="resume-details">
                                            <h4 className="resume-filename">{currentResumeFileName}</h4>
                                            <p className="resume-description">Current resume from profile</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleViewCurrentResume}
                                            className="resume-action-btn"
                                            title="View Resume"
                                            disabled={viewingResume}
                                            style={{
                                                marginRight: '8px',
                                                opacity: viewingResume ? 0.6 : 1
                                            }}
                                        >
                                            <i className={`fas ${viewingResume ? 'fa-spinner fa-spin' : 'fa-eye'}`}></i>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleDownloadCurrentResume}
                                            className="resume-action-btn"
                                            title="Download Resume"
                                            disabled={downloadingResume}
                                            style={{
                                                opacity: downloadingResume ? 0.6 : 1
                                            }}
                                        >
                                            <i className={`fas ${downloadingResume ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                                        </button>
                                    </div>
                                </div>
                            )}


                            {/* Upload New Resume */}
                            <div className="apply-form-field">
                                <label className="apply-form-label">Upload New Resume (Optional)</label>
                                <div className="file-upload-area">
                                    <i className={`fas ${uploadingResume ? 'fa-spinner fa-spin' : 'fa-cloud-upload-alt'} file-upload-icon`}></i>
                                    <p className="file-upload-text">
                                        {uploadingResume ? 'Uploading resume...' : 'Choose a different resume for this application'}
                                    </p>
                                    <input
                                        type="file"
                                        id="newResumeFile"
                                        accept=".pdf,.doc,.docx"
                                        className="file-upload-input"
                                        onChange={handleNewResumeUpload}
                                        disabled={submitting || uploadingResume}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => document.getElementById('newResumeFile').click()}
                                        className="file-choose-btn"
                                        disabled={submitting || uploadingResume}
                                        style={{
                                            opacity: uploadingResume ? 0.6 : 1,
                                            cursor: uploadingResume ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {uploadingResume ? 'Uploading...' : 'Choose File'}
                                    </button>
                                </div>
                            </div>

                        </div>
                        </div>

                        {/* Fixed Submit Section with Separator */}
                        <div style={{ 
                            borderTop: '1px solid #e5e7eb', 
                            padding: '16px 24px', 
                            backgroundColor: '#ffffff',
                            flexShrink: 0,
                            display: 'flex',
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                type="submit"
                                className="apply-submit-btn"
                                disabled={submitting}
                                aria-label={submitting ? 'Submitting application' : 'Apply now'}
                                style={{
                                    width: 'auto',
                                    minWidth: '150px',
                                    padding: '12px 24px',
                                    backgroundColor: submitting ? '#9ca3af' : '#2563eb',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    fontFamily: 'inherit',
                                    letterSpacing: '0',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                            >
                                <i className={`fas ${submitting ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
                                {submitting ? 'Submitting...' : 'Apply Now'}
                            </button>
                        </div>
                    </form>
                    )}
                </div>
            </div>
            
            {/* Inline Preview Overlay for current resume from profile */}
            {showCurrentResumePreview && currentResumeObjectUrl && (
                <div className="apply-modal-overlay" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setShowCurrentResumePreview(false)}>
                    <div className="apply-modal-container" style={{ maxWidth: '900px', width: '95%', height: '90vh' }} onClick={(e) => e.stopPropagation()}>
                        <div className="apply-modal-header">
                            <div className="apply-modal-header-content">
                                <div>
                                    <h2 className="apply-modal-title">Current Resume</h2>
                                    <p className="apply-modal-subtitle">{currentResumeFileName}</p>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <button
                                        onClick={handleDownloadCurrentResume}
                                        className="resume-action-btn"
                                        title="Download Resume"
                                        disabled={downloadingResume}
                                        style={{
                                            color: 'white',
                                            padding: '8px 12px',
                                            opacity: downloadingResume ? 0.6 : 1
                                        }}
                                    >
                                        <i className={`fas ${downloadingResume ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                                        {downloadingResume ? ' Downloading...' : ' Download'}
                                    </button>
                                    <button onClick={() => setShowCurrentResumePreview(false)} className="apply-modal-close-btn">
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="apply-modal-content" style={{ height: 'calc(90vh - 80px)' }}>
                            <iframe
                                title="Current Resume Preview"
                                src={currentResumeObjectUrl}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                            />
                        </div>
                    </div>
                </div>
            )}
            {/* Lightweight loader while downloading resume */}
            {downloadingResume && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
                    <div style={{ background: '#111827', color: '#fff', padding: '16px 20px', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.3)' }}>
                        <span className="spinner" style={{ width: 20, height: 20, border: '3px solid rgba(255,255,255,0.25)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
                        <span>Opening resume…</span>
                    </div>
                    {/* Inline keyframes to ensure spinner works without CSS file changes */}
                    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                </div>
            )}
        </div>
    );
};

export default ApplyNowModal;
